import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * api.ts — el contrato de la Biblioteca.
 *
 * ## Los dos endpoints NO cambian
 *
 *     GET  /api/resources    require_module("recursos")
 *     POST /api/resources    require_permission("recursos", "manage")
 *
 * Lo vigila `test_biblioteca_pantalla.py`.
 *
 * ## El que se queda mal, a propósito
 *
 * `POST /api/resources` recibe `title`, `description`, `resource_type` y
 * `external_url` como **query params**: son parámetros sueltos en la firma del
 * router (`content.py:1231`), y el archivo va aparte como multipart. Así que el
 * título y la descripción que alguien escribe viajan en la URL y **quedan
 * escritos en el log de accesos** de nginx, que registra la query string.
 * `Convenciones.md` lo prohíbe por nombre: con adjunto, lo que toca es
 * `Form(...)` + `File(...)`.
 *
 * **No se mueve aquí.** Cambiar la firma de un endpoint es un cambio de
 * contrato, no un rediseño. Va con su propio cambio, igual que `motivo` en
 * Novedades y el `PUT` de plantillas en Notificaciones, que siguen en la URL
 * con esta misma nota. Hay una prueba que obliga a que siga escrita.
 */

/* ── Tipos ─────────────────────────────────────────────────────────────── */

/** Los siete valores del Enum `resources.resource_type` (`content.py:1264`). */
export type TipoDeRecurso =
  | 'libro' | 'manual' | 'plantilla' | 'video' | 'enlace' | 'infografia' | 'podcast';

/** Lo que devuelve `_resource_dict` (`content.py:1727`) de cada recurso.
 *
 *  Las dos URL de archivo NO son intercambiables: `file_url` va con
 *  `disposition=inline` —para el visor— y `download_url` con `attachment`
 *  —para descargar—. `_resource_urls` además fuerza el Content-Type real,
 *  porque con `nosniff` un PDF servido como `octet-stream` no se pinta en un
 *  iframe. Usar una por otra rompe el visor o la descarga, sin error. */
export interface Recurso {
  id: number;
  title: string;
  description?: string | null;
  resource_type: TipoDeRecurso | string;
  category_id?: number | null;
  file_url?: string | null;
  download_url?: string | null;
  preview_url?: string | null;
  external_url?: string | null;
  created_at?: string | null;
}

/** Cuántos se piden. Era 100 en el vanilla y se conserva. El endpoint no
 *  devuelve total ni ordena (`offset/limit` sin `order_by`), así que de «los
 *  100» no se puede decir que sean los más recientes: solo que hay 100. */
export const POR_PAGINA = 100;

/* ── Claves de caché ───────────────────────────────────────────────────── */

export const claves = {
  recursos: ['biblioteca', 'recursos'] as const,
};

/* ── Lecturas ──────────────────────────────────────────────────────────── */

/** Toda la biblioteca de una vez. El filtro por tipo y la búsqueda se aplican
 *  en el cliente, como en el vanilla: son 100 filas, y así cambiar de filtro no
 *  cuesta una petición. */
export function useRecursos() {
  return useQuery({
    queryKey: claves.recursos,
    queryFn: () => api.get<Recurso[]>('/resources', { limit: POR_PAGINA }),
  });
}

/* ── Escrituras ────────────────────────────────────────────────────────── */

export interface CamposDeRecurso {
  title: string;
  description: string;
  resource_type: TipoDeRecurso;
  external_url?: string;
}

/** Crear un recurso.
 *
 *  Los campos van en la URL porque así lo declara el endpoint hoy —ver la nota
 *  de arriba—. El archivo, si lo hay, va en el cuerpo como multipart con el
 *  nombre `file`, que es el que espera `File(None)` en el router. */
export function useCrearRecurso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campos, archivo }: { campos: CamposDeRecurso; archivo: File | null }) => {
      const params = new URLSearchParams({
        title: campos.title,
        description: campos.description,
        resource_type: campos.resource_type,
        ...(campos.external_url ? { external_url: campos.external_url } : {}),
      });
      const ruta = `/resources?${params}`;
      if (!archivo) return api.post<{ id: number }>(ruta, null);
      const datos = new FormData();
      datos.append('file', archivo);
      return api.upload<{ id: number }>(ruta, datos);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: claves.recursos }),
  });
}
